import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlatformUser, StoreOption } from './Index';

type Props = {
    users: PlatformUser[];
    sortAsc: boolean;
    onToggleSort: () => void;
    onEdit: (u: PlatformUser) => void;
    onToggleActive: (u: PlatformUser) => void;
    onSendInvite: (u: PlatformUser) => void;
    onViewScopes: (u: PlatformUser) => void;
    storeMap: Record<string, StoreOption>;
};

type Rect = { left: number; top: number; width: number; height: number };

export function UsersTable({ users, sortAsc, onToggleSort, onEdit, onToggleActive, onSendInvite, onViewScopes, storeMap }: Props) {
    const [menuUser, setMenuUser] = useState<PlatformUser | null>(null);
    const [menuRect, setMenuRect] = useState<Rect | null>(null);
    const [btnRefs] = useState<Record<string, HTMLButtonElement | null>>({});

    function openMenu(u: PlatformUser) {
        const el = btnRefs[u._id];
        if (!el) return;
        const r = el.getBoundingClientRect();
        setMenuRect({ left: r.left, top: r.top, width: r.width, height: r.height });
        setMenuUser(u);
    }

    useEffect(() => {
        function onDoc(e: MouseEvent) {
            const t = e.target as HTMLElement | null;
            if (t && t.closest('.actions-menu__list')) return;
            if (t && t.closest('.actions__more')) return;
            if (menuUser) setMenuUser(null);
        }
        function onScroll() { if (menuUser) setMenuUser(null); }
        function onResize() { if (menuUser) setMenuUser(null); }
        document.addEventListener('mousedown', onDoc, true);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize, true);
        return () => {
            document.removeEventListener('mousedown', onDoc, true);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize, true);
        };
    }, [menuUser]);

    const portal = menuUser && menuRect ? createPortal(
        <Menu
            rect={menuRect}
            user={menuUser}
            onEdit={() => { onEdit(menuUser); setMenuUser(null); }}
            onToggle={() => { onToggleActive(menuUser); setMenuUser(null); }}
            onInvite={() => { onSendInvite(menuUser); setMenuUser(null); }}
        />,
        document.body
    ) : null;

    return (
        <>
            <table>
                <thead>
                    <tr>
                        <th className="sortable" onClick={onToggleSort}>Email {sortAsc ? '▲' : '▼'}</th>
                        <th>Scopes</th>
                        <th>Store</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(u => {
                        const storeId = u.stores?.[0];
                        const store = storeId ? storeMap[storeId] : undefined;
                        return (
                            <tr key={u._id}>
                                <td>{u.email}</td>
                                <td>
                                    <div className="scopes">
                                        {(u.scopes || []).slice(0, 2).map(v => (
                                            <span key={v} className="pill pill--scope">{v}</span>
                                        ))}
                                        {u.scopes && u.scopes.length > 2 && (
                                            <button className="pill pill--more" onClick={() => onViewScopes(u)}>
                                                +{u.scopes.length - 2}
                                            </button>
                                        )}
                                    </div>
                                </td>
                                <td className="store-col">
                                    {store ? (
                                        <a href={`/management/${store.id}`}><span className="store-chip"><span className="name">{store.name}</span></span></a>
                                    ) : (
                                        <span className="muted">—</span>
                                    )}
                                </td>
                                <td>
                                    {u.isActive ? (
                                        <span className="badge badge--active">Active</span>
                                    ) : (
                                        <span className="badge badge--disabled">Inactive</span>
                                    )}
                                </td>
                                <td className="actions">
                                    <button
                                        ref={el => { btnRefs[u._id] = el; }}
                                        className="actions__more"
                                        aria-haspopup="menu"
                                        aria-expanded={menuUser?._id === u._id}
                                        onClick={() => openMenu(u)}
                                        title="More"
                                    >
                                        ⋯
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                    {users.length === 0 && (
                        <tr>
                            <td className="empty" colSpan={5}>No results</td>
                        </tr>
                    )}
                </tbody>
            </table>
            {portal}
        </>
    );
}

function Menu({ rect, user, onEdit, onToggle, onInvite }: { rect: Rect; user: PlatformUser; onEdit: () => void; onToggle: () => void; onInvite: () => void; }) {
    const maxHeight = Math.min(260, Math.floor(window.innerHeight * 0.6));
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const openUp = spaceBelow < 160;
    const contentH = 132;
    const top = openUp ? rect.top - 8 - Math.min(contentH, maxHeight) : rect.top + rect.height + 8;

    const listStyle: React.CSSProperties = {
        position: 'fixed',
        left: rect.left + rect.width - 180,
        top,
        width: 180,
        maxHeight,
        zIndex: 14001,
    };

    return (
        <div className="actions-menu-portal">
            <div role="menu" className="actions-menu__list" style={listStyle}>
                <button role="menuitem" className="actions-menu__item" onClick={onEdit}>Edit</button>
                <button role="menuitem" className="actions-menu__item" onClick={onToggle}>{user.isActive ? 'Disable' : 'Enable'}</button>
                <button role="menuitem" className="actions-menu__item" onClick={onInvite}>Send link</button>
            </div>
        </div>
    );
}
